import { supabase } from "@/lib/supabase";

/**
 * Resolve ledger `reference_type` / `reference_id` links into human-readable
 * labels and navigable routes, so a stock movement can be traced back to the
 * business transaction that caused it.
 */

export interface ReferenceInfo {
  label: string;
  route: string | null;
}

export interface LedgerReference {
  reference_type: string | null;
  reference_id: string | null;
}

export function referenceKey(type: string | null, id: string | null): string | null {
  if (!type || !id) return null;
  return `${type}:${id}`;
}

export async function resolveLedgerReferences(
  orgId: string,
  rows: LedgerReference[],
): Promise<Map<string, ReferenceInfo>> {
  const map = new Map<string, ReferenceInfo>();
  const byType: Record<string, string[]> = {};
  for (const r of rows) {
    if (r.reference_type && r.reference_id) {
      (byType[r.reference_type] ??= []).push(r.reference_id);
    }
  }
  const uniq = (t: string) => Array.from(new Set(byType[t] ?? []));

  const salesIds = uniq("sales_order");
  const fulfilmentIds = uniq("fulfilment");
  const receiptIds = uniq("purchase_receipt");
  const transferIds = uniq("stock_transfer");
  const returnIds = uniq("sales_return");
  const sessionIds = uniq("stock_count_session");

  const [sos, fulfilments, receipts, transfers, returns, sessions] = await Promise.all([
    salesIds.length
      ? supabase.from("sales_orders").select("id, order_number").in("id", salesIds)
      : Promise.resolve({ data: [] }),
    fulfilmentIds.length
      ? supabase.from("fulfilments").select("id, fulfilment_number, sales_order_id").in("id", fulfilmentIds)
      : Promise.resolve({ data: [] }),
    receiptIds.length
      ? supabase.from("purchase_receipts").select("id, receipt_number, purchase_order_id").in("id", receiptIds)
      : Promise.resolve({ data: [] }),
    transferIds.length
      ? supabase.from("stock_transfers").select("id, transfer_number").in("id", transferIds)
      : Promise.resolve({ data: [] }),
    returnIds.length
      ? supabase.from("sales_returns").select("id, return_number").in("id", returnIds)
      : Promise.resolve({ data: [] }),
    sessionIds.length
      ? supabase.from("stock_count_sessions").select("id, session_number").in("id", sessionIds)
      : Promise.resolve({ data: [] }),
  ]);

  (sos.data ?? []).forEach((r: { id: string; order_number: string }) =>
    map.set(`sales_order:${r.id}`, { label: r.order_number, route: `/sales-orders/${r.id}` }),
  );
  (transfers.data ?? []).forEach((r: { id: string; transfer_number: string }) =>
    map.set(`stock_transfer:${r.id}`, { label: r.transfer_number, route: `/transfers/${r.id}` }),
  );
  (returns.data ?? []).forEach((r: { id: string; return_number: string }) =>
    map.set(`sales_return:${r.id}`, { label: r.return_number, route: `/returns/${r.id}` }),
  );
  (sessions.data ?? []).forEach((r: { id: string; session_number: string }) =>
    map.set(`stock_count_session:${r.id}`, { label: r.session_number, route: null }),
  );
  (fulfilments.data ?? []).forEach(
    (r: { id: string; fulfilment_number: string; sales_order_id: string | null }) =>
      map.set(`fulfilment:${r.id}`, {
        label: r.fulfilment_number,
        route: r.sales_order_id ? `/sales-orders/${r.sales_order_id}` : null,
      }),
  );
  (receipts.data ?? []).forEach(
    (r: { id: string; receipt_number: string; purchase_order_id: string | null }) =>
      map.set(`purchase_receipt:${r.id}`, {
        label: r.receipt_number,
        route: r.purchase_order_id ? `/purchase-orders/${r.purchase_order_id}` : null,
      }),
  );

  return map;
}

/**
 * Resolve audit-log `table_name` / `record_id` pairs into human-readable labels
 * and navigable routes, so "Recent Activity" and the audit trail show *what*
 * was changed rather than a raw UUID.
 */
export interface AuditReference {
  table_name: string;
  record_id: string;
}

export async function resolveAuditReferences(
  orgId: string,
  rows: AuditReference[],
): Promise<Map<string, ReferenceInfo>> {
  const map = new Map<string, ReferenceInfo>();
  const byTable: Record<string, Set<string>> = {};
  for (const r of rows) {
    if (!r.record_id) continue;
    (byTable[r.table_name] ??= new Set()).add(r.record_id);
  }

  const resolve = async (
    table: string,
    select: string,
    label: (row: Record<string, unknown>) => string,
    route: (row: Record<string, unknown>) => string | null,
  ) => {
    const ids = Array.from(byTable[table] ?? []);
    if (ids.length === 0) return;
    const { data } = await supabase
      .from(table as never)
      .select(select)
      .in("id", ids);
    const rows = Array.isArray(data) ? data : [];
    rows.forEach((row) => {
      if (typeof row !== "object" || row === null) return;
      const r = row as Record<string, unknown>;
      map.set(`${table}:${r.id}`, { label: label(r) || String(r.id), route: route(r) });
    });
  };

  await Promise.all([
    resolve("sales_orders", "id, order_number", (r) => String(r.order_number ?? ""), (r) => `/sales-orders/${r.id}`),
    resolve("purchase_orders", "id, po_number", (r) => String(r.po_number ?? ""), (r) => `/purchase-orders/${r.id}`),
    resolve("sales_returns", "id, return_number", (r) => String(r.return_number ?? ""), (r) => `/returns/${r.id}`),
    resolve("stock_transfers", "id, transfer_number", (r) => String(r.transfer_number ?? ""), (r) => `/transfers/${r.id}`),
    resolve("shipments", "id, tracking_number", (r) => String(r.tracking_number ?? `Shipment ${String(r.id).slice(0, 8)}`), (r) => `/fulfilment/shipments/${r.id}`),
    resolve("fulfilments", "id, fulfilment_number, sales_order_id", (r) => String(r.fulfilment_number ?? ""), (r) => (r.sales_order_id != null ? `/sales-orders/${r.sales_order_id}` : null)),
    resolve("purchase_receipts", "id, receipt_number, purchase_order_id", (r) => String(r.receipt_number ?? ""), (r) => (r.purchase_order_id != null ? `/purchase-orders/${r.purchase_order_id}` : null)),
    resolve("products", "id, name", (r) => String(r.name ?? ""), (r) => `/products/${r.id}`),
    resolve("customers", "id, name", (r) => String(r.name ?? ""), (r) => `/customers/${r.id}`),
    resolve("suppliers", "id, name", (r) => String(r.name ?? ""), (r) => `/suppliers/${r.id}`),
    resolve("locations", "id, name", (r) => String(r.name ?? ""), (r) => `/locations/${r.id}`),
    resolve("variants", "id, sku, product_id", (r) => String(r.sku ?? "Variant"), (r) => (r.product_id != null ? `/products/${r.product_id}` : null)),
  ]);

  return map;
}
